import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Dropdown, Empty, Tabs } from "antd";

import { checkPermissions } from "@comps/commonFunctions";
import "./base-section-tabs.css";

/**
 * Section tabs that never hide a section behind an anonymous "..." menu.
 *
 * antd's default overflow behaviour collapses the tabs that do not fit into a
 * bare-ellipsis dropdown. That costs users twice: the trigger carries no label
 * or count, and the active tab itself can end up inside it, so the strip stops
 * showing where you are. This wrapper replaces the tab bar (antd still owns the
 * panes, so mount/keep-alive behaviour is unchanged) with:
 *
 *  - wide container  -> a scrollable strip, every section reachable by scroll or
 *                       the edge arrows, active section always scrolled into view
 *  - narrow container -> a labelled switcher ("Roles ▾") listing every section in
 *                       one menu, with the current one marked
 *
 * The switch is driven by the container width (ResizeObserver), not the viewport,
 * because collapsing the workspace sidebar changes the space the strip actually has.
 */

const SWITCHER_MAX_WIDTH = 640;

const ChevronIcon = ({ direction }) => (
  <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
    <path
      d={ direction === "left" ? "M10.2 3.3 5.5 8l4.7 4.7" : "M5.8 3.3 10.5 8l-4.7 4.7" }
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const BaseSectionTabs = (props) => {
  const {
    items,
    activeKey,
    defaultActiveKey,
    onChange,
    className,
    extra,
    animated,
    destroyInactiveTabPane,
    switcherLabel = "Section",
  } = props;

  const containerRef = useRef(null);
  const listRef = useRef(null);
  const tabRefs = useRef({});

  // Controlled when the screen passes activeKey; otherwise the strip keeps its own
  // selection, so screens that never wired a tab state (employee profile) still work.
  const [internalKey, setInternalKey] = useState(defaultActiveKey);
  const isControlled = activeKey !== undefined;
  const [containerWidth, setContainerWidth] = useState(0);
  const [overflow, setOverflow] = useState({ left: false, right: false });

  const permittedItems = useMemo(() => (
    (items || []).filter((item) => {
      if (!item) return false;
      if (!item.modkey) return true;
      // No implicit "view" default: checkPermissions(modkey) also accepts a bare
      // module grant, and forcing the action here would hide sections the screens
      // used to show.
      return checkPermissions(item.modkey, item.action);
    })
  ), [items]);

  const selectedKey = isControlled ? activeKey : internalKey;
  const currentItem = permittedItems.find((item) => item.key === selectedKey) || permittedItems[0];
  const currentKey = currentItem?.key;
  const isSwitcher = containerWidth > 0 && containerWidth < SWITCHER_MAX_WIDTH;

  // Container width drives the strip/switcher choice — see the note above.
  useLayoutEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;
    setContainerWidth(node.clientWidth);
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver((entries) => {
      setContainerWidth(entries[0].target.clientWidth);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const syncOverflow = useCallback(() => {
    const node = listRef.current;
    if (!node) return;
    const maxScroll = node.scrollWidth - node.clientWidth;
    setOverflow({
      left: node.scrollLeft > 1,
      right: maxScroll > 1 && node.scrollLeft < maxScroll - 1,
    });
  }, []);

  useLayoutEffect(() => {
    if (isSwitcher) return undefined;
    const node = listRef.current;
    if (!node) return undefined;
    syncOverflow();
    node.addEventListener("scroll", syncOverflow, { passive: true });
    if (typeof ResizeObserver === "undefined") {
      return () => node.removeEventListener("scroll", syncOverflow);
    }
    const observer = new ResizeObserver(syncOverflow);
    observer.observe(node);
    return () => {
      node.removeEventListener("scroll", syncOverflow);
      observer.disconnect();
    };
  }, [isSwitcher, permittedItems.length, syncOverflow]);

  // Keep the active section on screen even when it sits outside the visible slice.
  // Adjusting scrollLeft by hand rather than scrollIntoView, so bringing a tab
  // into view never scrolls the page or an ancestor panel along with it.
  useEffect(() => {
    if (isSwitcher) return;
    const list = listRef.current;
    const node = tabRefs.current[currentKey];
    if (!list || !node) return;
    const left = node.offsetLeft;
    const right = left + node.offsetWidth;
    if (left < list.scrollLeft) {
      list.scrollLeft = Math.max(left - 12, 0);
    } else if (right > list.scrollLeft + list.clientWidth) {
      list.scrollLeft = right - list.clientWidth + 12;
    }
  }, [currentKey, isSwitcher, containerWidth]);

  const scrollByPage = (direction) => {
    const node = listRef.current;
    if (!node) return;
    node.scrollBy({ left: direction * Math.max(node.clientWidth * 0.8, 120), behavior: "smooth" });
  };

  const selectKey = (key) => {
    if (!key || key === currentKey) return;
    if (!isControlled) setInternalKey(key);
    onChange?.(key);
  };

  const onKeyNav = (event) => {
    const keys = permittedItems.map((item) => item.key);
    const index = keys.indexOf(currentKey);
    if (index < 0) return;
    let next = null;
    if (event.key === "ArrowRight") next = keys[(index + 1) % keys.length];
    else if (event.key === "ArrowLeft") next = keys[(index - 1 + keys.length) % keys.length];
    else if (event.key === "Home") next = keys[0];
    else if (event.key === "End") next = keys[keys.length - 1];
    if (!next) return;
    event.preventDefault();
    selectKey(next);
    tabRefs.current[next]?.focus();
  };

  if (!permittedItems.length) {
    return props.empty ?? <Empty description="No sections are available for your role." />;
  }

  const tabBar = isSwitcher ? (
    <div className="section-tabs__switcher">
      <Dropdown
        trigger={ ["click"] }
        overlayClassName="section-tabs__menu"
        menu={ {
          selectable: true,
          selectedKeys: [currentKey],
          items: permittedItems.map((item) => ({ key: item.key, label: item.label })),
          onClick: ({ key }) => selectKey(key),
        } }
      >
        <button type="button" className="section-tabs__switcher-button">
          <span className="section-tabs__switcher-label">{ switcherLabel }</span>
          <span className="section-tabs__switcher-value">{ currentItem?.label }</span>
          <span className="section-tabs__switcher-caret" aria-hidden="true">▾</span>
        </button>
      </Dropdown>
      { extra ? <div className="section-tabs__extra">{ extra }</div> : null }
    </div>
  ) : (
    <div className="section-tabs__bar">
      <div className={ `section-tabs__scroller${overflow.left ? " is-ping-left" : ""}${overflow.right ? " is-ping-right" : ""}` }>
        <button
          type="button"
          className="section-tabs__arrow section-tabs__arrow--left"
          onClick={ () => scrollByPage(-1) }
          disabled={ !overflow.left }
          tabIndex={ -1 }
          aria-hidden="true"
        >
          <ChevronIcon direction="left" />
        </button>

        <div className="section-tabs__list" ref={ listRef } role="tablist" aria-orientation="horizontal">
          { permittedItems.map((item) => (
            <button
              key={ item.key }
              type="button"
              role="tab"
              aria-selected={ item.key === currentKey }
              tabIndex={ item.key === currentKey ? 0 : -1 }
              ref={ (node) => { tabRefs.current[item.key] = node; } }
              className={ `section-tabs__tab${item.key === currentKey ? " is-active" : ""}` }
              onClick={ () => selectKey(item.key) }
              onKeyDown={ onKeyNav }
            >
              { item.icon ? <span className="section-tabs__tab-icon">{ item.icon }</span> : null }
              <span className="section-tabs__tab-label">{ item.label }</span>
            </button>
          )) }
        </div>

        <button
          type="button"
          className="section-tabs__arrow section-tabs__arrow--right"
          onClick={ () => scrollByPage(1) }
          disabled={ !overflow.right }
          tabIndex={ -1 }
          aria-hidden="true"
        >
          <ChevronIcon direction="right" />
        </button>
      </div>
      { extra ? <div className="section-tabs__extra">{ extra }</div> : null }
    </div>
  );

  return (
    <div className={ [ "section-tabs", className ].filter(Boolean).join(" ") } ref={ containerRef }>
      { tabBar }
      <Tabs
        className="section-tabs__panes"
        activeKey={ currentKey }
        onChange={ selectKey }
        animated={ animated }
        destroyInactiveTabPane={ destroyInactiveTabPane }
        renderTabBar={ () => <React.Fragment /> }
        items={ permittedItems.map((item) => ({
          key: item.key,
          label: item.label,
          children: item.children,
        })) }
      />
    </div>
  );
};

export default BaseSectionTabs;
